import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, FormActionBar, Panel, PanelSection, WindowContent, toast } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { ArchivableGuard, ArchiveToggleButton } from '@/shared/archival';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import {
  getGetVenuaPlannerProjectQueryKey,
  useArchiveVenuaPlannerProject,
  useGetVenuaPlannerProject,
  useUnarchiveVenuaPlannerProject,
} from '@/api/generated/venua-planner-planning/venua-planner-planning';
import { venuaPlannerQueryKeys } from '../../venuaPlannerApi';
import { DestinationSelectorField } from '@/modules/products/venua-planner/components/DestinationSelector';
import { ResponsiblePersonsPanel } from '@/modules/products/venua-planner/components/ResponsiblePersonsPanel';
import { projectFormAgenda } from './projectFormAgenda';
import { FORM_LABEL_WIDTH } from '@/modules/products/venua-planner/formLayout';
import { useProjectForm } from './useProjectForm';
import {
  emptyProjectFormValues,
  fromGetProjectResponse,
  type ProjectFormValues,
} from './projectFormSchema';

interface ProjectFormPageProps {
  layerId: string;
}

/**
 * Thin loader: vyřeší parametr agendy a (v editaci) načtení detailu projektu. Formulářovou část
 * vykreslí až s finálními výchozími hodnotami, takže `useForm` dostane správné defaulty hned
 * (žádný `form.reset`). Krátký `staleTime` — detail se při každém otevření načte znovu (viz GUIDELINES
 * „Detail stránky — čerstvost dat").
 */
export function ProjectFormPage({ layerId }: ProjectFormPageProps) {
  const { t } = useTranslation('venua-planner');
  const { open } = useAgendaParams(projectFormAgenda, layerId);
  const projectId = open.projectId;
  const isEdit = Boolean(projectId);

  const projectQuery = useGetVenuaPlannerProject(projectId ?? '', {
    query: { enabled: isEdit, staleTime: DETAIL_QUERY_STALE_TIME_MS },
  });

  if (isEdit && projectQuery.isLoading) {
    return (
      <p className="text-phx-text-muted text-phx-sm p-phx-5" data-testid="project-form">
        {t('project.loading')}
      </p>
    );
  }

  if (isEdit && projectQuery.isError) {
    return (
      <div className="p-phx-5" data-testid="project-form">
        <Alert severity="error" title={t('project.loadErrorTitle')}>
          {t('project.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <ProjectFormView
      layerId={layerId}
      projectId={projectId}
      initialValues={
        projectQuery.data ? fromGetProjectResponse(projectQuery.data) : emptyProjectFormValues()
      }
      isArchived={projectQuery.data?.isArchived ?? false}
    />
  );
}

interface ProjectFormViewProps {
  layerId: string;
  projectId?: string;
  initialValues: ProjectFormValues;
  isArchived: boolean;
}

/**
 * Vlastní formulář projektu nad TanStack Form. Panely jsou přes celou šířku okna, obsah je omezený
 * na čitelnou šířku a zarovnaný vlevo. Ukládání přes sdílený `FormActionBar` v patičce; archivace je
 * samostatná akce (v liště akcí, mimo read-only `ArchivableGuard`).
 */
function ProjectFormView({ layerId, projectId, initialValues, isArchived }: ProjectFormViewProps) {
  const { t } = useTranslation('venua-planner');
  const navigator = useNavigator();
  const queryClient = useQueryClient();
  const isEdit = Boolean(projectId);

  const closeForm = useCallback(() => {
    void navigator.closeLayer(layerId);
  }, [navigator, layerId]);

  const { form, submit } = useProjectForm({
    projectId,
    initialValues,
    onSaved: () => {
      // Detail (Orval klíč) — aby se po znovuotevření načetla čerstvá data; strom = seznamová vrstva
      // modulu. Viz GUIDELINES „Detail stránky — čerstvost dat".
      if (projectId) {
        void queryClient.invalidateQueries({
          queryKey: getGetVenuaPlannerProjectQueryKey(projectId),
        });
      }
      void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
      toast({ severity: 'success', description: t('project.toast.saved') });
    },
    onError: () => {
      toast({
        severity: 'error',
        title: t('project.toast.errorTitle'),
        description: t('project.toast.saveError'),
      });
    },
  });

  const archiveMutation = useArchiveVenuaPlannerProject();
  const unarchiveMutation = useUnarchiveVenuaPlannerProject();
  const archivalBusy = archiveMutation.isPending || unarchiveMutation.isPending;

  const invalidateAfterArchival = useCallback(() => {
    if (!projectId) return;
    void queryClient.invalidateQueries({ queryKey: getGetVenuaPlannerProjectQueryKey(projectId) });
    void queryClient.invalidateQueries({ queryKey: venuaPlannerQueryKeys.tree() });
  }, [queryClient, projectId]);

  const archivalError = useCallback(() => {
    toast({
      severity: 'error',
      title: t('project.toast.errorTitle'),
      description: t('project.toast.saveError'),
    });
  }, [t]);

  const handleArchive = useCallback(() => {
    if (!projectId) return;
    archiveMutation.mutate(
      { projectId },
      {
        onSuccess: () => {
          invalidateAfterArchival();
          toast({ severity: 'success', description: t('project.toast.archived') });
        },
        onError: archivalError,
      }
    );
  }, [projectId, archiveMutation, invalidateAfterArchival, archivalError, t]);

  const handleUnarchive = useCallback(() => {
    if (!projectId) return;
    unarchiveMutation.mutate(
      { projectId },
      {
        onSuccess: () => {
          invalidateAfterArchival();
          toast({ severity: 'success', description: t('project.toast.unarchived') });
        },
        onError: archivalError,
      }
    );
  }, [projectId, unarchiveMutation, invalidateAfterArchival, archivalError, t]);

  const title = isEdit ? t('project.titleEdit') : t('project.titleCreate');

  return (
    <WindowContent
      as="form"
      width="full"
      data-testid="project-form"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      footer={
        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            isSubmitting: state.isSubmitting,
          })}
        >
          {({ canSubmit, isSubmitting }) => (
            <FormActionBar
              onBack={closeForm}
              onSave={submit}
              saving={isSubmitting}
              disabled={isArchived || !canSubmit}
              statusNote={!canSubmit ? t('project.requiredHint') : undefined}
              extraActions={
                isEdit ? (
                  <ArchiveToggleButton
                    isArchived={isArchived}
                    onArchive={handleArchive}
                    onUnarchive={handleUnarchive}
                    disabled={archivalBusy}
                  />
                ) : undefined
              }
            />
          )}
        </form.Subscribe>
      }
    >
      <ArchivableGuard
        isArchived={isArchived}
        bannerDescription={t('project.archivedBannerDescription')}
        contentClassName="gap-phx-3 flex flex-col"
      >
        <Panel variant="filled" title={title}>
          <div className="w-full max-w-220">
            <PanelSection>
              <form.Field name="name">
                {(field) => (
                  <TextField
                    field={field}
                    label={t('project.name.label')}
                    required
                    layout="inline"
                    labelWidth={FORM_LABEL_WIDTH}
                    placeholder={t('project.name.placeholder')}
                    data-testid="project-name-input"
                  />
                )}
              </form.Field>
            </PanelSection>

            <PanelSection>
              <form.Field name="destinations">
                {(field) => (
                  <DestinationSelectorField
                    field={field}
                    mode="multi"
                    label={t('project.destinations.label')}
                    layout="inline"
                    labelWidth={FORM_LABEL_WIDTH}
                    placeholder={t('project.destinations.placeholder')}
                    testId="project-destinations"
                  />
                )}
              </form.Field>
            </PanelSection>
          </div>
        </Panel>

        <ResponsiblePersonsPanel
          form={form}
          title={t('project.persons.sectionLabel')}
          help={t('project.persons.help')}
          placeholder={t('project.persons.placeholder')}
          testIdPrefix="project"
          contentClassName="max-w-220"
        />
      </ArchivableGuard>
    </WindowContent>
  );
}
